import { Component } from '@verndale/core';
import { mediaQuery } from '../helpers/media-queries';
import { GenerateLoadSequence, LoadSequence } from '../helpers/animations';

class ImageAndTextCardsCarousel extends Component {
  cardsArray: Node[];
  contentIndex: number;
  currentSlide: number;
  previousSlide: number;
  leftPosition: number;
  rightPosition: number;
  nextSlide: number;

  timeout: NodeJS.Timeout | null;
  autoPlayTimeout: NodeJS.Timeout | null;

  touchStart: number;
  touchEnd: number;

  mq: MediaQueryList;

  autoplaySpeed: number;
  isPlaying: boolean;

  circle: HTMLElement | null;
  circumference: number | null;
  contentWidth: number;

  constructor(el: HTMLElement) {
    super(el);
    this.cardsArray = [];
    this.contentIndex = 0;
    this.currentSlide = 0;
    this.previousSlide = 0;
    this.leftPosition = 0;
    this.rightPosition = 0;
    this.nextSlide = 0;

    this.timeout = null;
    this.autoPlayTimeout = null;

    this.mq = mediaQuery('tablet');

    this.autoplaySpeed = this.el.dataset ? Number(this.el.dataset.playTime) : 6000;
    this.isPlaying = this.el.dataset ? this.el.dataset.autoPlay === 'true' : false;

    this.circle = null;
    this.circumference = null;
    this.touchStart = 0;
    this.touchEnd = 0;
    this.contentWidth = 0;

    this.duplicateCards();
    this.setActiveContent();
    this.setContentCardsHeight();
    this.setImageCardsHeight();
    this.setPagination();
    this.initCircle();
    this.loadAnimation();

    if (this.isPlaying) {
      this.autoPlayTimeout = setTimeout(() => {
        this.play();
      }, this.autoplaySpeed);
    }
  }

  setupDefaults() {
    this.dom = {
      cards: this.el.querySelectorAll('.image-and-text-cards-carousel__card'),
      carouselContainer: this.el.querySelector<HTMLElement>(
        '.image-and-text-cards-carousel__cards-wrapper'
      ),
      cardsContainer: this.el.querySelector<HTMLElement>(
        '.image-and-text-cards-carousel__content-container'
      ),
      contentWrapper: this.el.querySelector<HTMLElement>('.image-and-text-cards-carousel__cards-content'),
      contentCards: this.el.querySelectorAll('.image-and-text-card__content'),
      imageCardsContainer: this.el.querySelector<HTMLElement>('.image-and-text-cards-carousel__cards'),
      paginationContainer: this.el.querySelector<HTMLElement>(
        '.image-and-text-cards-carousel__pagination-items .secondary-copy'
      ),
      prevButton: this.el.querySelector<HTMLElement>(
        '.image-and-text-cards-carousel__control--previous'
      ),
      nextButton: this.el.querySelector<HTMLElement>(
        '.image-and-text-cards-carousel__control--next'
      ),
      paginationButton: this.el.querySelector<HTMLElement>(
        '.image-and-text-cards-carousel__pagination-controls'
      ),
      controls: this.el.querySelector<HTMLElement>('.image-and-text-cards-carousel__controls')
    };
  }

  addListeners() {
    (this.dom.nextButton as HTMLElement)?.addEventListener('click', () => {
      this.isPlaying = false;
      this.resetCircle();
      if (this.autoPlayTimeout) {
        clearTimeout(this.autoPlayTimeout);
      }
      if (this.timeout) {
        clearTimeout(this.timeout);
      }
      this.next();
      this.cleanLoaderAnimation();
    });

    (this.dom.prevButton as HTMLElement)?.addEventListener('click', () => {
      this.isPlaying = false;
      this.resetCircle();
      if (this.autoPlayTimeout) {
        clearTimeout(this.autoPlayTimeout);
      }
      if (this.timeout) {
        clearTimeout(this.timeout);
      }
      this.prev();
      this.cleanLoaderAnimation();
    });

    (this.dom.paginationButton as HTMLElement)?.addEventListener('click', () => {
      if (this.isPlaying) {
        this.pause();
      } else {
        this.play();
      }

      if (this.autoPlayTimeout) {
        clearTimeout(this.autoPlayTimeout);
      }
    });

    window.addEventListener('resize', () => {
      if (!this.mq.matches) {
        this.setImageCardsHeight();
        this.setContentCardsHeight();
      } else {
        (this.dom.carouselContainer as HTMLElement).style.removeProperty('blockSize');
        (this.dom.contentWrapper as HTMLElement).style.removeProperty('blockSize');
      }
    });
  }

  setImageCardsHeight() {
    if (!this.mq.matches) {
      const imageCards = [...this.dom.cards as NodeList].map(card => (card as HTMLElement).querySelector('.image-and-text-card__image') as HTMLElement);
      const maxHeight = Math.max(...imageCards.map(card => card.offsetHeight));

      if (maxHeight) {
        (this.dom.carouselContainer as HTMLElement).style.blockSize = `${maxHeight}px`;
      }
    }
  }

  setContentCardsHeight() {
    const contentCards = [...(this.dom.contentCards as NodeList)].map(card => (card as HTMLElement).querySelector('.module-header') as HTMLElement);
    const maxHeight = Math.max(...contentCards.map(card => (card as HTMLElement).offsetHeight));
    const contentWrapper = this.dom.contentWrapper as HTMLElement;

    (contentWrapper as HTMLElement).style.blockSize = `${maxHeight + 32}px`;

  }

  duplicateCards() {
    const cards = [...(this.dom.cards as NodeList)];
    const cardsContainer = this.dom.carouselContainer as HTMLElement;

    cards
      .slice(0, cards.length - 2)
      .forEach(card => cardsContainer.appendChild(card.cloneNode(true)));

    const firstCardClone = cards[cards.length - 1].cloneNode(true);
    const secondCardClone = cards[cards.length - 2].cloneNode(true);
    cardsContainer.insertBefore(firstCardClone, cards[0]);
    cardsContainer.insertBefore(secondCardClone, firstCardClone);

    const newCards = this.el.querySelectorAll('.image-and-text-cards-carousel__card');

    this.cardsArray = [...newCards];

    this.cardsArray.forEach((card, index) => {
      (card as HTMLElement).addEventListener('click', () => {
        this.positionSlide(index);
        const contentIndex = Number((card as HTMLElement).dataset.contentIndex);
        this.changeContent(contentIndex);
        this.cleanLoaderAnimation();
        this.isPlaying = false;
        this.resetCircle();
      });
    });

    this.initSlider();
  }

  initSlider() {
    (this.cardsArray[0] as HTMLElement).classList.add('prev-prev');
    (this.cardsArray[1] as HTMLElement).classList.add('prev');
    (this.cardsArray[2] as HTMLElement).classList.add('active');
    (this.cardsArray[3] as HTMLElement).classList.add('next');
    (this.cardsArray[4] as HTMLElement).classList.add('next-next');

    this.currentSlide = 2;
    this.nextSlide = 3;
    this.previousSlide = this.cardsArray.length - 1;

    this.leftPosition = this.previousSlide - 1;
    this.rightPosition = this.nextSlide + 1;
  }

  prev() {
    const contentCards = [...(this.dom.contentCards as NodeList)];
    if (this.currentSlide > 0) {
      this.positionSlide(this.currentSlide - 1);
    } else {
      this.positionSlide(this.cardsArray.length - 1);
    }

    if (this.contentIndex > 0) {
      this.changeContent(this.contentIndex - 1);
    } else {
      this.changeContent(contentCards.length - 1);
    }
  }

  next() {
    const contentCards = [...(this.dom.contentCards as NodeList)];

    if (this.currentSlide < this.cardsArray.length - 1) {
      this.positionSlide(this.currentSlide + 1);
    } else {
      this.positionSlide(0);
    }

    if (this.contentIndex < contentCards.length - 1) {
      this.changeContent(this.contentIndex + 1);
    } else {
      this.changeContent(0);
    }

    if (this.isPlaying) {
      this.setAnimationTimeout();
    }
  }

  positionSlide(index: number) {
    this.currentSlide = index;
    this.previousSlide = this.currentSlide - 1;
    this.nextSlide = this.currentSlide + 1;
    this.leftPosition = this.previousSlide - 1;
    this.rightPosition = this.nextSlide + 1;

    for (let i = 0; i < this.cardsArray.length; i++) {
      (this.cardsArray[i] as HTMLElement).classList.remove('active');
      (this.cardsArray[i] as HTMLElement).classList.remove('prev');
      (this.cardsArray[i] as HTMLElement).classList.remove('next');
      (this.cardsArray[i] as HTMLElement).classList.remove('prev-prev');
      (this.cardsArray[i] as HTMLElement).classList.remove('next-next');
    }

    if (this.nextSlide === this.cardsArray.length) {
      this.nextSlide = 0;
    }

    if (this.previousSlide === -1) {
      this.previousSlide = this.cardsArray.length - 1;
    }

    if (this.leftPosition === -2) {
      this.leftPosition = this.cardsArray.length - 2;
    }

    if (this.leftPosition === -1) {
      this.leftPosition = this.cardsArray.length - 1;
    }

    if (this.leftPosition === this.cardsArray.length) {
      this.leftPosition = 0;
    }

    if (this.leftPosition === this.cardsArray.length + 1) {
      this.leftPosition = 1;
    }

    if (this.rightPosition === -2) {
      this.rightPosition = this.cardsArray.length - 2;
    }

    if (this.rightPosition === -1) {
      this.rightPosition = this.cardsArray.length - 1;
    }

    if (this.rightPosition === this.cardsArray.length) {
      this.rightPosition = 0;
    }

    if (this.rightPosition === this.cardsArray.length + 1) {
      this.rightPosition = 1;
    }

    (this.cardsArray[this.currentSlide] as HTMLElement).classList.add('active');
    (this.cardsArray[this.previousSlide] as HTMLElement).classList.add('prev');
    (this.cardsArray[this.nextSlide] as HTMLElement).classList.add('next');
    (this.cardsArray[this.leftPosition] as HTMLElement).classList.add('prev-prev');
    (this.cardsArray[this.rightPosition] as HTMLElement).classList.add('next-next');
  }

  setActiveContent() {
    const contentCards = [...(this.dom.contentCards as NodeList)];
    (contentCards as HTMLElement[]).forEach(cardContent => {
      (cardContent as HTMLElement).classList.remove('image-and-text-card__content--active');
    });

    const currentCardContent = contentCards[this.contentIndex] as HTMLElement;

    currentCardContent.classList.add('image-and-text-card__content--active');
  }

  setPagination() {
    const contentCards = [...(this.dom.contentCards as NodeList)];
    (this.dom.paginationContainer as HTMLElement).innerHTML = `${this.contentIndex + 1}/${
      contentCards.length
    }`;
  }

  changeContent(index: number) {
    if (index === this.contentIndex) {
      return;
    }

    this.contentIndex = index;
    this.setPagination();
    this.setActiveContent();
  }

  initCircle() {
    this.circle = this.el.querySelector<HTMLElement>(
      '.inner-circle .image-and-text-cards-carousel__circle'
    );

    if (!this.circle) return;

    const r = this.circle.getAttribute('r');
    this.circumference = 2 * Math.PI * parseFloat(`${r}`);

    this.circle.style.strokeDasharray = `${this.circumference} ${this.circumference}`;
    this.circle.style.strokeDashoffset = `${this.circumference}`;
  }

  resetCircle() {
    if (!this.circle) return;
    this.circle.style.removeProperty('transition');
    this.circle.style.strokeDashoffset = `${this.circumference}`;
  }

  circleLoaderAnimation() {
    if (!this.circle) return;
    const animationDuration = (this.autoplaySpeed / 1000) * 2;

    this.circle.style.transition = `stroke-dashoffset ${animationDuration}s ease-in-out`;
    this.circle.style.strokeDashoffset = '0';

    setTimeout(() => {
      this.resetCircle();
    }, this.autoplaySpeed);
  }

  setAnimationTimeout() {
    this.circleLoaderAnimation();

    this.timeout = setTimeout(() => {
      this.next();
    }, this.autoplaySpeed + 100);
  }

  play() {
    const button = this.dom.paginationButton as HTMLElement;
    button.dataset.isPlaying = 'true';
    this.isPlaying = true;

    this.cleanLoaderAnimation();
    this.next();
  }

  pause() {
    const button = this.dom.paginationButton as HTMLElement;
    button.dataset.isPlaying = 'false';
    this.isPlaying = false;

    this.cleanLoaderAnimation();

    this.resetCircle();
  }

  cleanLoaderAnimation() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

  setTransition() {
    const cards = this.el.querySelectorAll('.image-and-text-cards-carousel__card');
    cards.forEach(card => {
      (card as HTMLElement).style.transition =
        'opacity 0.5s ease-in-out, transform 0.5s ease-in-out';
    });
  }

  loadAnimation() {
    const cardContent = [...(this.dom.contentCards as NodeList)][0] as HTMLElement;
    const eyebrow = cardContent.querySelector<HTMLElement>('.eyebrow');
    const title = cardContent.querySelector<HTMLElement>('.module-header__title');
    const content = cardContent.querySelector<HTMLElement>('.module-header__content');
    const links = cardContent.querySelectorAll('.link');
    const activeCard = this.el.querySelector<HTMLElement>(
      '.image-and-text-cards-carousel__card.active .image-and-text-card__image-container img'
    );
    const previousCard = this.el.querySelector<HTMLElement>(
      '.image-and-text-cards-carousel__card.prev .image-and-text-card__image-container img'
    );
    const nextCard = this.el.querySelector<HTMLElement>(
      '.image-and-text-cards-carousel__card.next .image-and-text-card__image-container img'
    );

    LoadSequence(this.el, [
      { element: eyebrow as HTMLElement, addScale: false, offset: 0 },
      { element: title as HTMLElement, addScale: false, offset: 0.1 },
      { element: content as HTMLElement, addScale: false, offset: 0.2 },
      ...GenerateLoadSequence([...links] as HTMLElement[], 3),
      { element: this.dom.controls as HTMLElement, addScale: false, offset: 0.6 },
      { element: previousCard as HTMLElement, addScale: false, offset: 0.3 },
      { element: activeCard as HTMLElement, addScale: false, offset: 0.4 },
      { element: nextCard as HTMLElement, addScale: false, offset: 0.4 }
    ]);

    this.setTransition();
  }
}

export default ImageAndTextCardsCarousel;
